/* ════════════════════════════════════════════════════════════════
   Hero — an 80s comic cover: a sunburst / halftone backdrop
   (ComicScene), the name as flat inked comic lettering that stamps
   in line by line, and the interactive copy.
   ════════════════════════════════════════════════════════════════ */
import { useEffect, useRef } from 'react';
import { FiGithub, FiLinkedin, FiMail } from 'react-icons/fi';
import { TypeOnce } from '../ui/primitives';
import ComicScene from '../three/ComicScene';
import { GITHUB_URL, LINKEDIN_URL, MAILTO } from '../data/site';

/* Scroll-linked parallax for the comic backdrop: it trails the page at
   a slower rate and dissolves before the About section arrives, so the
   panel melts into the site background instead of hitting a hard edge. */
function useBackdropParallax() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const h = window.innerHeight || 1;
      const p = Math.min(1, window.scrollY / (h * 0.85));
      el.style.transform = `translate3d(0, ${(p * h * 0.25).toFixed(1)}px, 0)`;
      el.style.opacity = Math.max(0, 1 - p * 1.15).toFixed(3);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, []);
  return ref;
}

export default function Hero({ ready }) {
  const backdropRef = useBackdropParallax();
  return (
    <section className="hero" id="top">
      {/* outer layer holds the edge-fade mask in place; the inner layer
          is what the parallax hook moves, so the scene drifts under a
          stationary fade instead of dragging a hard edge with it */}
      <div className="hero-backdrop" aria-hidden="true">
        <div className="hero-backdrop-drift" ref={backdropRef}>
          {ready && <ComicScene />}
        </div>
      </div>
      <div className="hero-inner">
        <p className="hero-eyebrow">{'// hello, world — I\'m'}</p>
        <h1 className={`hero-name${ready ? ' hero-name--in' : ''}`}>
          <span className="hero-name-line hero-name-line--first">Navendra</span>
          <span className="hero-name-line hero-name-line--last">Ramdhan</span>
        </h1>
        <p className="hero-role">
          {ready && <TypeOnce text="Front-End Software Engineer" speed={55} />}
        </p>
        <p className="hero-sub">I build clean, performant, interactive web experiences.</p>
        <div className="hero-cta">
          <a href="#projects" className="btn btn-primary">View My Work</a>
          <a href="#contact" className="btn btn-outline">Get In Touch</a>
        </div>
        <div className="hero-socials">
          <a href={GITHUB_URL} target="_blank" rel="noreferrer" aria-label="GitHub" title="GitHub"><FiGithub /></a>
          <a href={LINKEDIN_URL} target="_blank" rel="noreferrer" aria-label="LinkedIn" title="LinkedIn"><FiLinkedin /></a>
          <a href={MAILTO} aria-label="Email" title="Email me"><FiMail /></a>
        </div>
      </div>
      <a href="#about" className="scroll-cue" aria-label="Scroll to About section">
        <span>scroll</span>
        <span className="scroll-cue-line" />
      </a>
    </section>
  );
}
